import { useCallback } from "react";
import { useSearchParams } from "~/hooks/useSearchParams";

export const usePagination = (options?: {
  defaultPage?: number;
  defaultPageSize?: number;
  prefix?: string;
}) => {
  const { defaultPage = 1, defaultPageSize = 10, prefix = "" } = options || {};
  const [params, setSearchParams] = useSearchParams();

  const pageParamName = prefix ? `${prefix}Page` : "page";
  const pageSizeParamName = prefix ? `${prefix}PageSize` : "pageSize";

  const pageParam = Number.parseInt(params.get(pageParamName) ?? "", 10);
  const pageSizeParam = Number.parseInt(
    params.get(pageSizeParamName) ?? "",
    10,
  );

  // Firestore throws on limit(<= 0), during render, taking the app down.
  const page = pageParam > 0 ? pageParam : defaultPage;
  const pageSize = pageSizeParam > 0 ? pageSizeParam : defaultPageSize;

  const paginationSearchParams = useCallback(
    (opts: { page?: number | null; pageSize?: number | null }) => {
      const newParams = new URLSearchParams(params.toString());
      (
        [
          [pageParamName, defaultPage, opts.page],
          [pageSizeParamName, defaultPageSize, opts.pageSize],
        ] as const
      ).forEach(([name, defaultValue, value]) => {
        if (value === undefined) {
          return;
        }
        if (value === null || value === defaultValue) {
          newParams.delete(name);
        } else {
          newParams.set(name, String(value));
        }
      });
      return newParams;
    },
    [params, defaultPage, defaultPageSize, pageParamName, pageSizeParamName],
  );

  // Navigating to the current URL pushes a duplicate history entry and drops the forward stack.
  const navigateToParams = useCallback(
    (newParams: URLSearchParams) => {
      if (newParams.toString() === params.toString()) {
        return;
      }
      setSearchParams(newParams);
    },
    [setSearchParams, params],
  );

  const setPage = useCallback(
    (value: number) =>
      navigateToParams(paginationSearchParams({ page: value })),
    [navigateToParams, paginationSearchParams],
  );

  const setPageSize = useCallback(
    (value: number) =>
      navigateToParams(paginationSearchParams({ pageSize: value })),
    [navigateToParams, paginationSearchParams],
  );

  const link = useCallback(
    (opts: Parameters<typeof paginationSearchParams>[0]) =>
      paginationSearchParams(opts).toString(),
    [paginationSearchParams],
  );

  const reset = useCallback(
    () =>
      navigateToParams(
        paginationSearchParams({
          page: defaultPage,
          pageSize: defaultPageSize,
        }),
      ),
    [navigateToParams, paginationSearchParams, defaultPage, defaultPageSize],
  );

  return {
    setPage,
    setPageSize,
    page,
    pageSize,
    link,
    reset,
  };
};
